/* ORDER ENTRY FROM THE TOOLS THEMSELVES. Every field of every form is read from the tool's JSON Schema
   (toolSchemas() on the server), so a tool added to the seat appears in the console with its own fields,
   bounds and choices, and nothing here names a tool or an order. Values are kept as the strings a form
   holds and turned into arguments only when the call is placed; anything the schema would refuse is
   reported here first, with the server's own validation still the last word. */
import type { JsonSchema, ToolSchema } from './types.ts'

export type Field =
  | { key: string; kind: 'enum'; label: string; required: boolean; options: string[]; description?: string }
  | { key: string; kind: 'multi'; label: string; required: boolean; options: string[]; description?: string }
  | { key: string; kind: 'number'; label: string; required: boolean; integer: boolean; min?: number; max?: number; description?: string }
  | { key: string; kind: 'boolean'; label: string; required: boolean; description?: string }
  | { key: string; kind: 'text'; label: string; required: boolean; maxLength?: number; long: boolean; description?: string }
  | { key: string; kind: 'json'; label: string; required: boolean; description?: string }

export type FormValues = Record<string, string | string[] | boolean | undefined>

/** "vt_ml_per_kg" → "Vt ml per kg"; acronyms the ICU uses keep their case. */
const UPPER = new Set(['icu', 'map', 'peep', 'fio2', 'abg', 'cbc', 'bmp', 'ecg', 'cxr', 'ct', 'pa', 'lft', 'hfnc', 'niv', 'sbt', 'pci', 'crrt', 'piv', 'pocus', 'vap', 'iv', 'im', 'sc', 'ffp', 'hit', 'ps', 'vt', 'uf', 'poc', 'rt', 'ml', 'kg', 'lpm'])
export function humanise(id: string): string {
  const words = id.split('_').filter(Boolean)
  return words
    .map((w, i) => {
      if (UPPER.has(w.toLowerCase())) return w.toLowerCase() === 'fio2' ? 'FiO2' : w.toLowerCase() === 'ml' ? 'mL' : w.toLowerCase() === 'kg' ? 'kg' : w.toUpperCase()
      return i === 0 ? w[0].toUpperCase() + w.slice(1) : w
    })
    .join(' ')
}

const typeOf = (s: JsonSchema): string | undefined => (Array.isArray(s.type) ? s.type.find((t) => t !== 'null') : s.type)

/** The schema of a property, unwrapping a nullable anyOf. */
function unwrap(s: JsonSchema): JsonSchema {
  const alt = s.anyOf ?? s.oneOf
  if (alt?.length) {
    const real = alt.filter((x) => typeOf(x) !== 'null')
    if (real.length === 1) return { ...real[0], description: s.description ?? real[0].description }
  }
  return s
}

export function fieldsOf(tool: ToolSchema): Field[] {
  const props = tool.input_schema.properties ?? {}
  const required = new Set(tool.input_schema.required ?? [])
  const fields = Object.entries(props).map(([key, raw]): Field => {
    const s = unwrap(raw)
    const base = { key, label: humanise(key), required: required.has(key), ...(s.description ? { description: s.description } : {}) }
    const t = typeOf(s)
    if (s.enum) return { ...base, kind: 'enum', options: s.enum.map(String) }
    if (t === 'array' && s.items?.enum) return { ...base, kind: 'multi', options: s.items.enum.map(String) }
    if (t === 'number' || t === 'integer') return { ...base, kind: 'number', integer: t === 'integer', min: s.minimum ?? s.exclusiveMinimum, max: s.maximum ?? s.exclusiveMaximum }
    if (t === 'boolean') return { ...base, kind: 'boolean' }
    if (t === 'string') return { ...base, kind: 'text', maxLength: s.maxLength, long: (s.maxLength ?? 0) > 200 }
    return { ...base, kind: 'json' }
  })
  // Required first, then the schema's own order.
  return [...fields.filter((f) => f.required), ...fields.filter((f) => !f.required)]
}

export type Built = { args: Record<string, unknown>; errors: Record<string, string> }

/** The arguments a form's values amount to, or why the schema would refuse them. */
export function buildArgs(fields: readonly Field[], values: FormValues): Built {
  const args: Record<string, unknown> = {}
  const errors: Record<string, string> = {}
  for (const f of fields) {
    const v = values[f.key]
    const empty = v === undefined || v === '' || (Array.isArray(v) && v.length === 0)
    if (empty) {
      if (f.required && f.kind !== 'boolean') errors[f.key] = 'Required.'
      continue
    }
    switch (f.kind) {
      case 'enum':
        if (!f.options.includes(String(v))) errors[f.key] = 'Choose one of the options.'
        else args[f.key] = String(v)
        break
      case 'multi': {
        const xs = (Array.isArray(v) ? v : [String(v)]).filter((x) => f.options.includes(x))
        if (xs.length) args[f.key] = xs
        break
      }
      case 'number': {
        const n = Number(v)
        if (!Number.isFinite(n)) errors[f.key] = 'A number.'
        else if (f.integer && !Number.isInteger(n)) errors[f.key] = 'A whole number.'
        else if (f.min !== undefined && n < f.min) errors[f.key] = `At least ${f.min}.`
        else if (f.max !== undefined && n > f.max) errors[f.key] = `At most ${f.max}.`
        else args[f.key] = n
        break
      }
      case 'boolean':
        if (typeof v === 'boolean') args[f.key] = v
        break
      case 'text': {
        const s = String(v).trim()
        if (!s && f.required) errors[f.key] = 'Required.'
        else if (f.maxLength !== undefined && s.length > f.maxLength) errors[f.key] = `At most ${f.maxLength} characters.`
        else if (s) args[f.key] = s
        break
      }
      case 'json':
        try {
          args[f.key] = JSON.parse(String(v))
        } catch {
          errors[f.key] = 'JSON.'
        }
        break
    }
  }
  return { args, errors }
}

/** Two tools read alike once set in words: hand_off gives the patient to a human clinician, handoff writes the note for the next team. Each is named for what it does. */
const TOOL_WORDS: Record<string, string> = { hand_off: 'Hand the patient to a clinician', handoff: 'Write the handoff note' }

/** The words a tool is shown in: its own plain name where two would read alike, else its id set in words. The id stays in a tooltip. */
export const toolLabel = (name: string): string => TOOL_WORDS[name] ?? humanise(name)

/** Groups of the physician's seat (Plan 12 §8.1), read from a tool's name; a tool that fits none is Other. */
export const GROUPS = ['Look', 'Ask', 'Order', 'Device', 'Act', 'Communicate', 'Time', 'Other'] as const
export type Group = (typeof GROUPS)[number]

export function groupOf(name: string): Group {
  if (name === 'wait') return 'Time'
  if (/^(get_|read_|examine|ultrasound|subscribe_)/.test(name)) return 'Look'
  if (/^(ask_|talk_|consult)/.test(name)) return 'Ask'
  if (/^(order_|stop_order|set_protocol)/.test(name)) return 'Order'
  if (/^set_(ventilator|oxygen|crrt|alarm|monitoring)/.test(name)) return 'Device'
  if (/^(intubate|extubate|perform_|call_code|prone|transfer|admit|discharge|assign_bed|decline_|reprioritise|request_)/.test(name)) return 'Act'
  if (/^(write_|handoff|hand_off|set_code_status|page|escalate)/.test(name)) return 'Communicate'
  return 'Other'
}

export function groupTools(tools: readonly ToolSchema[]): { group: Group; tools: ToolSchema[] }[] {
  return GROUPS.map((g) => ({ group: g, tools: tools.filter((t) => groupOf(t.name) === g) })).filter((x) => x.tools.length)
}

/** A tool that takes no arguments is placed in one click. */
export const isInstant = (tool: ToolSchema): boolean => Object.keys(tool.input_schema.properties ?? {}).length === 0

/** One line for a call, for the feed: "order_lab · test lactate, stat". */
export function describeCall(name: string, args: unknown): string {
  if (!args || typeof args !== 'object') return toolLabel(name)
  const parts = Object.entries(args as Record<string, unknown>).map(([k, v]) => (typeof v === 'string' && ['drug', 'test', 'fluid', 'procedure', 'order', 'topic', 'service', 'to'].includes(k) ? humanise(v) : typeof v === 'string' && v.length > 60 ? `${k}: “${v.slice(0, 57)}…”` : `${humanise(k).toLowerCase()} ${Array.isArray(v) ? v.join(', ') : String(v)}`))
  return parts.length ? `${toolLabel(name)} · ${parts.join(', ')}` : toolLabel(name)
}
